<script setup lang="ts">
import { ref, watch } from 'vue';

import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import { RunSheetTableData } from '@/models/reports';

export interface Props {
  modelValue?: RunSheetTableData | null;
  loading?: boolean;
  handleDriverSelected?: (idx: number, driver: string) => void;
  handleDaySelected?: (idx: string) => void;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  loading: false,
  handleDriverSelected: undefined,
  handleDaySelected: undefined,
});
const emit = defineEmits<{
  'update:modelValue': [value?: RunSheetTableData | null];
  'handleDriverSelected': [day: string, driver: string];
  'handleDaySelected': [day: string];
}>();

const fieldValue = ref(props.modelValue);

watch(
  () => props.modelValue,
  (newModelValue) => {
    fieldValue.value = newModelValue;
    emit('update:modelValue', fieldValue.value);
  },
  { immediate: true },
);
</script>

<template>
  <v-expansion-panels v-if="fieldValue" variant="accordion" multiple flat>
    <v-expansion-panel
      v-for="(day, idx) in Object.keys(fieldValue.daysCheckboxes)"
      :key="idx"
      elevation="0"
      class="elevation-0 m-0 mb-8 p-0"
      :class="$style.expansionPanel"
    >
      <v-expansion-panel-title
        class="flex w-40 items-center rounded border-none px-3"
        :class="[
          $style.expansionPanelTitle,
          !!fieldValue.daysCheckboxes[day] ? $style.expansionPanelTitleBg : '',
        ]"
      >
        <KCheckboxField
          v-model="fieldValue.daysCheckboxes[day]"
          :label="day"
          hide-details
          :class="$style.checkboxField"
          class="w-40"
          @click.stop
          @change="emit('handleDaySelected', day)"
        />
        <div class="text-base"
          >Total
          {{ Object.values(fieldValue.driverCheckboxes[day]).length }}</div
        >
        <div
          v-if="
            Object.values(fieldValue.driverCheckboxes[day]).filter(Boolean)
              .length >= 1
          "
          class="mx-2 text-base"
          :class="$style.selectedCount"
        >
          ({{
            Object.values(fieldValue.driverCheckboxes[day]).filter(Boolean)
              .length
          }})
        </div>
      </v-expansion-panel-title>
      <v-expansion-panel-text :class="$style.expansionPanelText">
        <div class="grid grid-cols-4 py-2">
          <div
            v-for="(driver, id) in fieldValue.reports[day]"
            :key="id"
            class=""
          >
            <KCheckboxField
              v-model="fieldValue.driverCheckboxes[day][Number(driver[0])]"
              :label="`${driver[0]} ${driver[1]}`"
              hide-details
              :class="$style.checkboxField"
              @change="emit('handleDriverSelected', day, driver[1])"
            />
          </div>
        </div>
      </v-expansion-panel-text>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.selectedCount {
  color: colors.$color-blue-600;
}

.expansionPanelTitle {
  background-color: colors.$color-brand-100;

  :global {
    .v-expansion-panel-title__overlay {
      opacity: 0 !important;
    }
  }
}

.expansionPanelTitleBg {
  background-color: colors.$color-brand-200;
}

  .checkboxField {
    :global {
      .expansionPanelTitle .v-expansion-panel-title__overlay {
        opacity: 1;
      }
      .v-input__control {
        width: min-content;
        .v-label {
          white-space: nowrap;
        }
      }
    }
  }
</style>
